import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { COMPANY } from "@/data/company";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState("login"); // login | forgot
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setForgotSent(true); // ne pas révéler l'existence du compte
    } finally { setForgotLoading(false); }
  };

  const inputCls = "w-full mt-1 rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-white text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={fileUrl(COMPANY.logo)} alt="SMT Provence" className="h-16 mx-auto mb-4 bg-white/5 rounded-lg p-2" />
          <h1 className="font-heading text-2xl font-black text-white">Espace administrateur</h1>
          <p className="text-white/50 text-sm mt-1">Back office SMT Provence</p>
        </div>

        {mode === "login" ? (
          <form onSubmit={submit} data-testid="login-form" className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-white/60">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className={inputCls} placeholder="admin@smtprovence.fr" />
            </div>
            <div>
              <label className="text-xs font-semibold text-white/60">Mot de passe</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls} placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-full hover:brightness-95 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />} Se connecter
            </button>
            <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setForgotEmail(email); }}
              className="w-full text-center text-xs text-white/50 hover:text-primary transition-colors">
              Mot de passe oublié ?
            </button>
          </form>
        ) : (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8" data-testid="forgot-form">
            {forgotSent ? (
              <div className="text-center">
                <p className="text-white font-semibold">Demande envoyée</p>
                <p className="text-white/60 text-sm mt-2">Si un compte est associé à cette adresse, vous recevrez un email contenant un lien de réinitialisation (valable 1 heure).</p>
                <button onClick={() => { setMode("login"); setForgotSent(false); }} className="mt-5 text-primary text-sm font-semibold hover:underline" data-testid="back-to-login">Retour à la connexion</button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-4">
                <div>
                  <h2 className="font-heading font-bold text-white text-lg">Mot de passe oublié</h2>
                  <p className="text-white/50 text-xs mt-1">Saisissez votre email, nous vous enverrons un lien de réinitialisation.</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-white/60">Email</label>
                  <input data-testid="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                    className={inputCls} placeholder="admin@smtprovence.fr" />
                </div>
                <button type="submit" disabled={forgotLoading} data-testid="forgot-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-full hover:brightness-95 disabled:opacity-60">
                  {forgotLoading ? <Loader2 className="animate-spin" size={18} /> : null} Envoyer le lien
                </button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-center text-xs text-white/50 hover:text-primary transition-colors">
                  Retour à la connexion
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
